import {
	ReactElement,
	RefObject,
	useEffect,
	useMemo,
	useRef
} from "react";
import {
	useResizeObserver,
	useStableImperativeCallback
} from "client/Hooks";
import {
	clamp,
	isNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import styles from "./FocalPointEditor.module.less";
import { FocalPoint } from "./FocalPointDialog";
import { isTouchEvent } from "client/Misc/domUtils";

export type FocalPointEditorProps = {
	uri: string;
	value: FocalPoint;
	onChange: (focalPoint: FocalPoint) => void;
};

export const FocalPointEditor = (props: FocalPointEditorProps): ReactElement => {
	const {
		uri,
		value,
		onChange
	} = props;

	const isMovingEnabledRef: RefObject<boolean> = useRef<boolean>(false);

	const imageRef: RefObject<HTMLImageElement | null> = useRef<HTMLImageElement>(null);
	const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const focalPointRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const { width: imageWidth, height: imageHeight } = useResizeObserver(imageRef);
	const { width: rootWidth, height: rootHeight } = useResizeObserver(rootRef);

	const stableOnChange: typeof onChange = useStableImperativeCallback(onChange);

	useEffect(
		(): void | (() => void) => {
			const cancellation = new AbortController();
			const { signal } = cancellation;

			if (isNil(imageRef.current) || isNil(focalPointRef.current)) {
				return;
			}

			const imageElement: HTMLImageElement = imageRef.current;
			const focalPointElement: HTMLDivElement = focalPointRef.current;

			const onPointerMove = (args: MouseEvent | TouchEvent) => {
				if (!isMovingEnabledRef.current) {
					return;
				}

				let clientX: number;
				let clientY: number;

				if (isTouchEvent(args)) {
					clientX = args.touches[0]
						.clientX;
					clientY = args.touches[0]
						.clientY;
				} else {
					clientX = args.clientX;
					clientY = args.clientY;
				}

				const imageBoundingRectangle: DOMRect = imageElement.getBoundingClientRect();

				const xPercent: number = (clientX - imageBoundingRectangle.x) / imageBoundingRectangle.width;
				const yPercent: number = (clientY - imageBoundingRectangle.y) / imageBoundingRectangle.height;

				const focalPoint: FocalPoint = {
					left: clamp(
						xPercent,
						0,
						1
					),
					top: clamp(
						yPercent,
						0,
						1
					)
				};

				stableOnChange(focalPoint);
			};

			const onFocalPointPointerDown = (): void => {
				isMovingEnabledRef.current = true;
			};

			const onImagePointerDown = (args: MouseEvent | TouchEvent): void => {
				isMovingEnabledRef.current = true;

				onPointerMove(args);
			};

			const onPointerUp = (): void => {
				isMovingEnabledRef.current = false;
			};

			document.addEventListener(
				"mousemove",
				onPointerMove,
				{ signal }
			);

			document.addEventListener(
				"touchmove",
				onPointerMove,
				{
					passive: false,
					signal
				}
			);

			document.addEventListener(
				"mouseup",
				onPointerUp,
				{ signal }
			);

			document.addEventListener(
				"touchend",
				onPointerUp,
				{ signal }
			);

			imageElement.addEventListener(
				"mousedown",
				onImagePointerDown,
				{ signal }
			);

			imageElement.addEventListener(
				"touchstart",
				onImagePointerDown,
				{ signal }
			);

			focalPointElement.addEventListener(
				"mousedown",
				onFocalPointPointerDown,
				{ signal }
			);

			focalPointElement.addEventListener(
				"touchstart",
				onFocalPointPointerDown,
				{ signal }
			);

			return (): void => {
				cancellation.abort();
			};
		},
		[stableOnChange]
	);

	const mappedFocalPointPosition = useMemo(
		(): FocalPoint | undefined => {
			hasImplicitDependencyOn(
				imageWidth,
				imageHeight,
				rootWidth,
				rootHeight
			);

			if (isNil(imageRef.current) || isNil(rootRef.current)) {
				return;
			}

			const imageRect: DOMRect = imageRef.current
				.getBoundingClientRect();
			const containerRect: DOMRect = rootRef.current
				.getBoundingClientRect();

			const xPixels: number = (value.left * imageRect.width);
			const yPixels: number = (value.top * imageRect.height);

			const focalPointXRelativeToContainer: number = xPixels + imageRect.x - containerRect.x;
			const focalPointYRelativeToContainer: number = yPixels + imageRect.y - containerRect.y;

			const clampedX: number = clamp(
				focalPointXRelativeToContainer,
				imageRect.x - containerRect.x,
				imageRect.right - containerRect.x
			);

			const clampedY: number = clamp(
				focalPointYRelativeToContainer,
				imageRect.y - containerRect.y,
				imageRect.bottom - containerRect.y
			);

			return {
				left: clampedX / containerRect.width,
				top: clampedY / containerRect.height,
			};
		},
		[
			imageWidth,
			imageHeight,
			rootWidth,
			rootHeight,
			value.left,
			value.top
		]
	);

	return (
		<div
			ref={rootRef}
			className={styles.root}
		>
			<img
				src={uri}
				className={styles.image}
				ref={imageRef}
				draggable={false}
			/>
			<div
				style={{
					"--_focal-point-y": mappedFocalPointPosition?.top,
					"--_focal-point-x": mappedFocalPointPosition?.left
				}}
				className={styles.focalPoint}
				ref={focalPointRef}
			/>
		</div>
	);
};
FocalPointEditor.displayName = "FocalPointEditor";